import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'

// Import AccountForm statically to avoid dynamic import issues
import AccountForm from '../components/account/AccountForm.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/campaigns',
    name: 'Campaigns',
    component: () => import('@/views/Campaigns.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/campaigns/create',
    name: 'CampaignCreate',
    component: () => import('../components/campaigns/CampaignForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/campaigns/:id/edit',
    name: 'CampaignEdit',
    component: () => import('../components/campaigns/CampaignForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/campaigns/:id',
    name: 'CampaignDetail',
    component: () => import('@/views/CampaignDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/templates',
    name: 'Templates',
    component: () => import('@/views/Templates.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/templates/create',
    name: 'TemplateCreate',
    component: () => import('../components/templates/TemplateForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/templates/:id/edit',
    name: 'TemplateEdit',
    component: () => import('../components/templates/TemplateForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/categories',
    name: 'Categories',
    component: () => import('@/views/Categories.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/subcategories',
    name: 'Subcategories',
    component: () => import('@/views/Subcategories.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/advertisers',
    name: 'Advertisers',
    component: () => import('@/views/Advertisers.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/channel-prices',
    name: 'ChannelPrices',
    component: () => import('@/views/ChannelPrices.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/creativity-field-locks',
    name: 'CreativityFieldLocks',
    component: () => import('@/views/CreativityFieldLocks.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/policies',
    name: 'Policies',
    component: () => import('@/views/Policies.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/advertisers/create',
    name: 'AdvertiserCreate',
    component: () => import('../components/advertisers/AdvertiserForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/advertisers/:id/edit',
    name: 'AdvertiserEdit',
    component: () => import('../components/advertisers/AdvertiserForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/advertisers/:id',
    name: 'AdvertiserDetail',
    component: () => import('@/views/AdvertiserDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/accounts',
    name: 'Accounts',
    component: () => import('@/views/Accounts.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/accounts/create',
    name: 'AccountCreate',
    component: AccountForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/accounts/:id/edit',
    name: 'AccountEdit',
    component: AccountForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/accounts/:id',
    name: 'AccountDetail',
    component: () => import('@/views/AccountDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/msisdn-validators',
    name: 'MsisdnValidators',
    component: () => import('@/views/MsisdnValidators.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/msisdn-validators/create',
    name: 'MsisdnValidatorCreate',
    component: () => import('../components/tools/MsisdnValidatorForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/msisdn-validators/:id/edit',
    name: 'MsisdnValidatorEdit',
    component: () => import('../components/tools/MsisdnValidatorForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/views/Users.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profiles',
    name: 'Profiles',
    component: () => import('@/views/Profiles.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profiles/create',
    name: 'ProfileCreate',
    component: () => import('../components/profiles/ProfileForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profiles/:id/edit',
    name: 'ProfileEdit',
    component: () => import('../components/profiles/ProfileForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profiles/:id',
    name: 'ProfileDetail',
    component: () => import('@/views/ProfileDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/operators',
    name: 'Operators',
    component: () => import('@/views/Operators.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/operators/create',
    name: 'OperatorCreate',
    component: () => import('../components/operators/OperatorForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/operators/:id/edit',
    name: 'OperatorEdit',
    component: () => import('../components/operators/OperatorForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/operators/:id',
    name: 'OperatorDetail',
    component: () => import('@/views/OperatorDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/countries',
    name: 'Countries',
    component: () => import('@/views/Countries.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/countries/create',
    name: 'CountryCreate',
    component: () => import('../components/countries/CountryForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/countries/:id/edit',
    name: 'CountryEdit',
    component: () => import('../components/countries/CountryForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/countries/:id',
    name: 'CountryDetail',
    component: () => import('@/views/CountryDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/scenario-types',
    name: 'ScenarioTypes',
    component: () => import('@/views/ScenarioTypes.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/scenario-types/create',
    name: 'ScenarioTypeCreate',
    component: () => import('../components/scenario-types/ScenarioTypeForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/scenario-types/:id/edit',
    name: 'ScenarioTypeEdit',
    component: () => import('../components/scenario-types/ScenarioTypeForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/scenario-types/:id',
    name: 'ScenarioTypeDetail',
    component: () => import('@/views/ScenarioTypeDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/tools',
    name: 'Tools',
    component: () => import('@/views/Tools.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/tools/entel-stats',
    name: 'EntelStats',
    component: () => import('@/views/EntelStats.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/agent',
    name: 'Agent',
    component: () => import('@/views/AgentView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/services',
    name: 'Services',
    component: () => import('@/views/Services.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/sim-cards',
    name: 'SimCards',
    component: () => import('@/views/SimCards.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/sim-cards/create',
    name: 'SimCardCreate',
    component: () => import('../components/simCards/SimCardForm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/sim-cards/:id',
    name: 'SimCardDetail',
    component: () => import('@/views/SimCardDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/audit-log',
    name: 'AuditLog',
    component: () => import('@/views/AuditLog.vue'),
    meta: { requiresAuth: true, requiresPrivilege: 'audit/index' }
  },
  {
    path: '/my-account',
    name: 'MyAccount',
    component: () => import('@/views/MyAccount.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Wait for auth initialization to complete if it's in progress
  if (authStore.initializing) {
    // Wait for initialization to complete
    while (authStore.initializing) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
  }
  
  // If not initialized yet, initialize auth
  if (!authStore.initialized) {
    try {
      await authStore.initializeAuth()
    } catch (error) {
      console.error('Error during auth initialization:', error)
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next('/')
    return
  }

  // Check privilege-gated routes
  if (to.meta.requiresPrivilege) {
    const userStore = useUserStore()
    if (!userStore.hasPrivilege(to.meta.requiresPrivilege as string)) {
      next('/')
      return
    }
  }

  next()
})

export default router
